import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import usePageContent from './usePageContent';
import PageHeroEditor from './PageHeroEditor';
import LivePreviewPanel, { MembershipPreview } from './LivePreviewPanel';

const defaults = {
  hero: {
    title: 'Join',
    highlight: 'PEA PNG',
    description:
      'Become a member and enjoy exclusive benefits, professional development opportunities, and a strong collective voice.',
  },
  benefits: {
    heading: 'Membership Benefits',
    items: [
      {
        title: 'Legal Protection',
        body_markdown: 'Access to legal advice and representation for employment-related matters.',
      },
      {
        title: 'Professional Development',
        body_markdown: 'Training programs, workshops, and seminars to enhance your skills and career.',
      },
      {
        title: 'Collective Bargaining',
        body_markdown: 'Strong representation in negotiations for better working conditions and benefits.',
      },
    ],
  },
  form: {
    heading: 'Membership Application',
    subheading: 'Fill in your details to join our association',
  },
};

const MembershipContentEditor = () => {
  const [showPreview, setShowPreview] = useState(false);
  const { loading, saving, sections, setSections, saveSection } = usePageContent({
    pageName: 'membership',
    defaultsBySection: defaults,
  });

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Live Preview Panel */}
      <LivePreviewPanel 
        pageName="membership" 
        showPreview={showPreview} 
        setShowPreview={setShowPreview}
      >
        <MembershipPreview content={sections} />
      </LivePreviewPanel>

      <PageHeroEditor
        title={sections.hero.title}
        setTitle={(v) => setSections({ ...sections, hero: { ...sections.hero, title: v } })}
        highlight={sections.hero.highlight}
        setHighlight={(v) => setSections({ ...sections, hero: { ...sections.hero, highlight: v } })}
        description={sections.hero.description}
        setDescription={(v) => setSections({ ...sections, hero: { ...sections.hero, description: v } })}
        onSave={() => saveSection('hero')}
        saving={saving}
      />

      <Card>
        <CardHeader>
          <CardTitle>Benefits</CardTitle>
          <CardDescription>3 cards (Markdown supported)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={sections.benefits.heading}
              onChange={(e) => setSections({ ...sections, benefits: { ...sections.benefits, heading: e.target.value } })}
              className="mt-1"
            />
          </div>

          {(sections.benefits.items || []).slice(0, 3).map((item, idx) => (
            <div key={idx} className="border rounded-xl p-4 bg-gray-50 space-y-3">
              <p className="font-semibold">Card {idx + 1}</p>
              <div>
                <Label>Title</Label>
                <Input
                  value={item.title}
                  onChange={(e) => {
                    const next = [...sections.benefits.items];
                    next[idx] = { ...next[idx], title: e.target.value };
                    setSections({ ...sections, benefits: { ...sections.benefits, items: next } });
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Body (Markdown)</Label>
                <Textarea
                  value={item.body_markdown}
                  onChange={(e) => {
                    const next = [...sections.benefits.items];
                    next[idx] = { ...next[idx], body_markdown: e.target.value };
                    setSections({ ...sections, benefits: { ...sections.benefits, items: next } });
                  }}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
          ))}

          <Button
            type="button"
            onClick={() => saveSection('benefits')}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save Benefits
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Form Intro</CardTitle>
          <CardDescription>Text shown above the membership form</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={sections.form.heading}
              onChange={(e) => setSections({ ...sections, form: { ...sections.form, heading: e.target.value } })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Subheading</Label>
            <Textarea
              value={sections.form.subheading}
              onChange={(e) => setSections({ ...sections, form: { ...sections.form, subheading: e.target.value } })}
              className="mt-1"
              rows={2}
            />
          </div>

          <Button
            type="button"
            onClick={() => saveSection('form')}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save Form Intro
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipContentEditor;
